import { getAccessToken, isRefreshFailure, refreshOnce, setAccessToken } from '../../lib/api.js';
import { useAuthStore } from './auth.store.js';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3001';

/**
 * Special sentinel error thrown when a 401 survives the refresh attempt.
 * Surfaces a user-facing Hungarian message in place of the raw backend
 * "Invalid token" string; downstream UI components can also branch on
 * `isSessionExpired` to redirect or show a re-login modal.
 */
export class SessionExpiredError extends Error {
  isSessionExpired = true;
  constructor() {
    super('A munkamenet lejárt — jelentkezz be újra a folytatáshoz.');
    this.name = 'SessionExpiredError';
  }
}

/**
 * Thrown on HTTP 429. Typed (and flagged, like {@link SessionExpiredError}) so
 * the React Query retry policy can refuse to retry it — see
 * `lib/query-retry.ts`. Retrying a rate limit is the one case where another
 * attempt is guaranteed to make things worse.
 *
 * Thrown here rather than in each feature's `jsonOrThrow` because `authFetch` is
 * the single choke point every request goes through: one place to change, and
 * every endpoint in the app gets a 429 the retry policy can recognise.
 */
export class RateLimitedError extends Error {
  isRateLimited = true;
  readonly status = 429;
  /** From `Retry-After`, or null when the header is absent/unreadable. */
  readonly retryAfterSeconds: number | null;
  constructor(retryAfterSeconds: number | null) {
    super(
      retryAfterSeconds === null
        ? 'Túl sok kérés — várj egy kicsit, majd próbáld újra.'
        : `Túl sok kérés — próbáld újra ${retryAfterSeconds} másodperc múlva.`,
    );
    this.name = 'RateLimitedError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Parse `Retry-After` (delta-seconds form, which is what @nestjs/throttler
 * sends). Returns null when absent, unparseable, or hidden from JS.
 *
 * In production web + api are same-origin behind the nginx `/api` proxy, so the
 * header is readable. In cross-origin dev it is not exposed by CORS and this
 * yields null — which is fine: the 429 is refused a retry either way, the header
 * only sharpens the message shown to the user.
 *
 * NOTE: named throttlers suffix the header (`Retry-After-auth`); the endpoints
 * that produce these floods all use the unnamed `default` throttler.
 */
export function parseRetryAfterSeconds(res: Response): number | null {
  const raw = res.headers.get('Retry-After');
  if (raw === null) return null;
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isSafeInteger(n) ? n : null;
}

/**
 * Shared authenticated fetch used by feature APIs and shared components.
 * Transparently handles access-token refresh on 401:
 *   1. attach current token (if any)
 *   2. on 401, try the refresh cookie once
 *   3. if refresh succeeds → retry the original request with the new token
 *   4. if refresh fails → clear session AND throw SessionExpiredError so
 *      the calling mutation surfaces a friendly Hungarian message
 *      (instead of the raw "Invalid token") and the AuthGate kicks the
 *      user back to /login on the next render.
 */
export async function authFetch(path: string, init?: RequestInit): Promise<Response> {
  const send = async (token: string | null): Promise<Response> => {
    const headers = new Headers(init?.headers);
    if (token) headers.set('authorization', `Bearer ${token}`);
    if (init?.body && !(init.body instanceof FormData) && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }
    return fetch(`${BASE_URL}${path}`, { ...init, headers, credentials: 'include' });
  };

  const first = await send(getAccessToken());
  // 429 becomes a typed throw so the retry policy can recognise it. Callers that
  // inspect `res.status` themselves (e.g. the 404 → null in getPlanByMonth) are
  // unaffected: a 429 already ended up as a thrown Error via their jsonOrThrow,
  // this only makes it a *recognisable* one.
  if (first.status === 429) throw new RateLimitedError(parseRetryAfterSeconds(first));
  if (first.status !== 401) return first;

  const refreshed = await refreshOnce();
  if (isRefreshFailure(refreshed)) {
    // A throttled refresh is not an expired session. Clearing the session here
    // would log the user out for being busy — and, worse, the resulting login
    // round-trip adds more requests to the bucket that is already full.
    if (refreshed.reason === 'rate_limited') {
      throw new RateLimitedError(refreshed.retryAfterSeconds);
    }
    setAccessToken(null);
    useAuthStore.getState().clear();
    throw new SessionExpiredError();
  }
  // Update the session store so AuthGate stays "authenticated" and the
  // rest of the app picks up the new user record.
  useAuthStore.getState().setSession(refreshed.accessToken, refreshed.user);
  const retried = await send(refreshed.accessToken);
  // Same guard on the post-refresh attempt — a token refresh does not exempt the
  // request from the rate limit.
  if (retried.status === 429) throw new RateLimitedError(parseRetryAfterSeconds(retried));
  return retried;
}
